import {Button, Controls, ControlsItem, Divider, Typography} from '@genestack/ui';
import React, {useState} from 'react';

import {
    useApiAccessQuery,
    useCreateApiAccessMutation,
    useDeleteApiAccessMutation
} from '../../hooks/user/use-api-access-query';
import {useConfirm} from '../confirm/use-confirm';
import {ApiAccessCredentialsResponse} from '../../../typings/user';

import {Preloader} from '../preloader/preloader';
import {CredentialRow} from './credential-row';
import {UsageGuide} from './usage-guide';
import styles from './api-access.module.css';

interface Props {
    userId: string;
}

export function ApiAccessSection({userId}: Props) {
    const apiAccessQuery = useApiAccessQuery(userId);
    const createMutation = useCreateApiAccessMutation(userId);
    const deleteMutation = useDeleteApiAccessMutation(userId);
    const [createdCredentials, setCreatedCredentials] =
        useState<ApiAccessCredentialsResponse | null>(null);

    const handleCreate = () => {
        createMutation.mutate(undefined, {
            onSuccess: (data) => setCreatedCredentials(data)
        });
    };

    const handleRevoke = useConfirm({
        title: 'Revoke API access?',
        message: 'The secret and any existing tokens will stop working immediately.',
        confirmBtnText: 'Revoke',
        cancelBtnText: 'Cancel',
        onConfirm: () => {
            deleteMutation.mutate(undefined, {
                onSuccess: () => setCreatedCredentials(null)
            });
        }
    });

    const handleRegenerate = useConfirm({
        title: 'Regenerate secret?',
        message: 'The old secret and any existing tokens will stop working immediately.',
        confirmBtnText: 'Regenerate',
        cancelBtnText: 'Cancel',
        onConfirm: () => {
            createMutation.mutate(undefined, {
                onSuccess: (data) => setCreatedCredentials(data)
            });
        }
    });

    const apiAccess = apiAccessQuery.data;
    const hasAccess = apiAccess?.enabled;

    return (
        <>
            <Divider variant="transparent" gap={3} />

            <Typography variant="header" intent="quiet">
                API access
            </Typography>

            <Divider variant="transparent" gap={2} />

            <Preloader show={apiAccessQuery.isLoading} count={1} wrapAll="div" wrapEach="div">
                {!hasAccess && !createdCredentials && (
                    <>
                        <Typography intent="quiet">No API credentials configured.</Typography>
                        <Divider variant="transparent" />
                        <Button onClick={handleCreate} disabled={createMutation.isPending}>
                            Enable API access
                        </Button>
                    </>
                )}

                {(hasAccess || createdCredentials) && (
                    <>
                        <CredentialRow
                            label="Client ID"
                            value={apiAccess?.clientId ?? createdCredentials?.clientId}
                        />
                        <Divider variant="transparent" />

                        {createdCredentials ? (
                            <>
                                <CredentialRow
                                    label="Client Secret"
                                    value={createdCredentials.clientSecret}
                                />
                                <Divider variant="transparent" />
                                <Typography intent="alarm">
                                    Save the secret now. You won&apos;t be able to see it again.
                                </Typography>
                            </>
                        ) : (
                            <Controls gap={1}>
                                <ControlsItem>
                                    <Typography
                                        as="span"
                                        intent="quiet"
                                        className={styles.credentialLabel}
                                    >
                                        Client Secret:
                                    </Typography>
                                </ControlsItem>
                                <ControlsItem>
                                    <Typography
                                        as="span"
                                        intent="quiet"
                                        className={styles.hiddenSecret}
                                    >
                                        Hidden - regenerate to get a new one
                                    </Typography>
                                </ControlsItem>
                            </Controls>
                        )}

                        <Divider variant="transparent" gap={2} />

                        <Controls gap={1}>
                            <ControlsItem>
                                <Button
                                    onClick={handleRegenerate}
                                    disabled={createMutation.isPending}
                                >
                                    Regenerate secret
                                </Button>
                            </ControlsItem>
                            <ControlsItem>
                                <Button
                                    intent="alarm"
                                    onClick={handleRevoke}
                                    disabled={deleteMutation.isPending}
                                >
                                    Revoke access
                                </Button>
                            </ControlsItem>
                        </Controls>

                        <Divider variant="transparent" gap={2} />

                        <UsageGuide
                            clientId={(apiAccess?.clientId ?? createdCredentials?.clientId)!}
                        />
                    </>
                )}
            </Preloader>
        </>
    );
}
